import { motion } from 'framer-motion';
import { ShieldCheck, Award, Globe2, BadgeCheck, Landmark, GraduationCap, Medal } from 'lucide-react';
import { MaskedLines, FadeUp, Overline, EASE } from '@/components/Reveal';
import Marquee from '@/components/Marquee';
import { ENDORSEMENTS, ALLIANCE_MARQUEE, IMAGES } from '@/data/content';

const INSTITUTION_ICONS = { aes: GraduationCap, fgu: Landmark, cel: Medal };

const WEIGHT_POINTS = [
  {
    icon: Award,
    title: 'Expediente sobresaliente',
    desc: 'Competencias prácticas y pruebas tangibles de capacidad que las mejores universidades exigen.',
  },
  {
    icon: Globe2,
    title: 'Peso real en EE.UU. y el mundo',
    desc: 'Certificaciones con validez internacional, respaldadas desde Florida, Estados Unidos.',
  },
  {
    icon: ShieldCheck,
    title: 'Ventaja en admisiones',
    desc: 'Un perfil académico y de liderazgo que diferencia a su hijo de miles de postulantes.',
  },
];

const CERT_POINTS = [
  'Certificado oficial del Programa Future Leaders',
  'Aval de American Elite School y Florida Global University',
  'Certificación del Center of Education and Leadership',
  'Respaldo oficial: Licencia #3180 del Departamento de Educación de Florida',
];

export default function Aval() {
  return (
    <main>
      {/* ============ PAGE HERO ============ */}
      <section data-testid="aval-hero" className="relative flex min-h-[88vh] items-end overflow-hidden">
        <motion.img
          src={IMAGES.flag}
          alt="Bandera de Estados Unidos"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="aval-flag-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/30" />
        <div className="noise absolute inset-0" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-20 pt-40 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}>
            <Overline className="text-gold">Triple alianza internacional</Overline>
          </motion.div>
          <MaskedLines
            as="h1"
            delay={0.25}
            className="mt-7 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            lines={[
              <>Aval internacional</>,
              <>con <span className="text-crimson">peso real</span></>,
            ]}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg"
          >
            Esta triple alianza garantiza que las certificaciones de su hijo cuenten con el máximo
            estándar de excelencia académica y tengan peso real en Estados Unidos y el mundo.
          </motion.p>
        </div>
      </section>

      <div className="bg-gold text-navy">
        <Marquee items={ALLIANCE_MARQUEE} testId="aval-marquee" />
      </div>

      {/* ============ INSTITUCIONES ============ */}
      <section data-testid="institutions-section" className="bg-porcelain py-16 text-navy sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <Overline className="text-crimson">Las instituciones</Overline>
          </FadeUp>
          <MaskedLines
            inView
            className="mt-6 max-w-3xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
            lines={[<>Tres instituciones,</>, <>una <span className="font-serif normal-case italic text-crimson">garantía</span></>]}
          />

          <div className="mt-14 grid gap-6 md:grid-cols-3 lg:gap-8">
            {ENDORSEMENTS.map((e, i) => {
              const Icon = INSTITUTION_ICONS[e.id];
              return (
                <FadeUp key={e.id} delay={0.1 + i * 0.12}>
                  <article
                    data-testid={`institution-card-${e.id}`}
                    className="group relative h-full overflow-hidden rounded-2xl border border-navy/10 bg-white p-6 transition-all duration-500 hover:-translate-y-1.5 hover:border-crimson/40 hover:shadow-[0_24px_60px_-20px_rgba(217,27,92,0.35)] sm:p-8"
                  >
                    <span className="text-stroke-navy pointer-events-none absolute -right-2 -top-6 select-none font-display text-8xl font-extrabold">
                      0{i + 1}
                    </span>
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-crimson/10 text-crimson transition-colors duration-500 group-hover:bg-crimson group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <p className="mt-6 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-crimson">{e.role}</p>
                    <h3 className="mt-2 font-display text-xl font-extrabold leading-tight sm:text-2xl">{e.name}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-navy/60">{e.desc}</p>
                    {e.license && (
                      <span className="mt-5 inline-block rounded-full bg-gold/25 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-navy">
                        {e.license}
                      </span>
                    )}
                  </article>
                </FadeUp>
              );
            })}
          </div>

          <FadeUp delay={0.25}>
            <figure className="group relative mt-14 overflow-hidden rounded-2xl" data-testid="fgu-campus-figure">
              <img
                src={IMAGES.fguCampus}
                alt="Sede de Florida Global University en Florida"
                className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-96"
              />
              <figcaption className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-navy/90 to-transparent px-6 pb-5 pt-16 text-white">
                <Landmark className="h-5 w-5 text-gold" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Sede de Florida Global University · Florida, EE.UU.
                </span>
              </figcaption>
            </figure>
          </FadeUp>
        </div>
      </section>

      {/* ============ PESO ACADÉMICO ============ */}
      <section data-testid="weight-section" className="noise relative overflow-hidden bg-navy py-16 sm:py-24 lg:py-32">
        <span aria-hidden className="text-stroke-white pointer-events-none absolute -left-6 bottom-0 select-none font-display text-[18vw] font-extrabold uppercase leading-none">
          GLOBAL
        </span>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <FadeUp>
                <Overline className="text-gold">Validez académica</Overline>
              </FadeUp>
              <MaskedLines
                inView
                className="mt-6 font-serif text-3xl italic leading-[1.1] sm:text-4xl lg:text-5xl"
                lines={[<>“Peso real en</>, <>Estados Unidos</>, <>y el mundo.”</>]}
              />
              <FadeUp delay={0.2}>
                <p className="mt-8 max-w-lg text-base leading-relaxed text-white/65">
                  Un programa avalado desde Florida que transforma el esfuerzo de su hijo en un
                  expediente académico reconocido internacionalmente.
                </p>
              </FadeUp>
            </div>
            <div className="lg:col-span-6">
              <div className="space-y-0">
                {WEIGHT_POINTS.map((w, i) => (
                  <FadeUp key={w.title} delay={0.1 + i * 0.1}>
                    <div className="group flex items-start gap-5 border-t border-white/10 py-7 last:border-b transition-colors duration-500 hover:bg-white/[0.03]">
                      <span className="font-display text-xl font-extrabold text-crimson">0{i + 1}</span>
                      <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
                        <w.icon className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="font-display text-lg font-bold sm:text-xl">{w.title}</h3>
                        <p className="mt-1.5 max-w-md text-sm leading-relaxed text-white/55">{w.desc}</p>
                      </div>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CERTIFICADO ============ */}
      <section data-testid="certificate-section" className="bg-porcelain py-16 text-navy sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <FadeUp className="order-2 lg:order-1 lg:col-span-5">
              <div className="noise relative mx-auto max-w-sm overflow-hidden rounded-2xl bg-navy p-8">
                <img
                  src="/images/cert-tablet.jpg"
                  alt="Certificado del programa Future Leaders por American Elite School"
                  className="w-full mix-blend-screen"
                  data-testid="certificate-image"
                />
              </div>
            </FadeUp>
            <div className="order-1 lg:order-2 lg:col-span-7">
              <FadeUp>
                <Overline className="text-crimson">Certificación oficial</Overline>
              </FadeUp>
              <MaskedLines
                inView
                className="mt-6 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
                lines={[<>Un certificado que</>, <><span className="text-crimson">abre puertas</span></>]}
              />
              <FadeUp delay={0.15}>
                <ul className="mt-9 space-y-4">
                  {CERT_POINTS.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm leading-relaxed text-navy/75 sm:text-base">
                      <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-crimson" />
                      {c}
                    </li>
                  ))}
                </ul>
              </FadeUp>
              <FadeUp delay={0.25}>
                <div className="mt-10 inline-flex flex-wrap items-center gap-4 rounded-2xl border border-gold/60 bg-gold/15 px-6 py-5" data-testid="license-badge">
                  <img src={IMAGES.flag} alt="Bandera de Estados Unidos" className="h-9 w-14 rounded-[3px] object-cover" />
                  <div>
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy/60">Respaldo oficial</p>
                    <p className="font-display text-base font-extrabold text-navy sm:text-lg">
                      Licencia #3180 · Departamento de Educación de Florida
                    </p>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
